type MeasurableItem = {
  textValue: string;
  itemCount?: number;
};

const VISIBLE_ITEMS_AND_COUNTER_GAP = 4;
const MIN_FIRST_LABEL_WIDTH = 50;
const TEXT_FIT_MARGIN = 10; // margin of error for text fitting calculations

const createItemLabelElement = (
  item: MeasurableItem,
  index: number,
  total: number,
): HTMLSpanElement => {
  const itemElement = document.createElement('span');
  itemElement.textContent = `${item.textValue}${index < total - 1 ? ', ' : ''}`;
  itemElement.setAttribute('aria-hidden', 'true');
  itemElement.style.display = 'inline-block';
  return itemElement;
};

const getItemsCount = (items: (MeasurableItem | null)[]) =>
  items.reduce((count, item) => count + (item?.itemCount ?? 1), 0);

const calculateCounterWidth = (
  selectedItems: (MeasurableItem | null)[],
  itemsContainer: HTMLDivElement,
): number => {
  const hiddenItemsCounterSpan = document.createElement('span');
  hiddenItemsCounterSpan.textContent = `and ${getItemsCount(
    selectedItems.slice(1),
  )} more`; // Reserve space for the worst case where only the first preview is visible.
  hiddenItemsCounterSpan.style.display = 'inline-block';
  itemsContainer.appendChild(hiddenItemsCounterSpan);
  // eslint-disable-next-line @sail/ui-no-reflow
  return hiddenItemsCounterSpan.offsetWidth;
};

const getInitialVisibleItemsParams = (
  selectedItems: (MeasurableItem | null)[],
  availableWidth: number,
  itemsRef: React.MutableRefObject<React.MutableRefObject<HTMLSpanElement>[]>,
  itemsContainer: HTMLDivElement,
) => {
  let visibleItemsWidth = 0;
  let lastVisibleItemIndex = selectedItems.length - 1;

  // Create temporary elements for item labels to retrieve width
  for (let i = 0; i <= lastVisibleItemIndex; i++) {
    if (selectedItems[i]) {
      const itemLabel = createItemLabelElement(
        selectedItems[i]!,
        i,
        selectedItems.length,
      );
      itemsContainer.appendChild(itemLabel);

      // Items are visible until the accumulated width is greater than the available width
      // eslint-disable-next-line @sail/ui-no-reflow
      if (i > 0 && visibleItemsWidth + itemLabel.offsetWidth > availableWidth) {
        lastVisibleItemIndex = i - 1;
      } else {
        // eslint-disable-next-line @sail/ui-no-reflow
        visibleItemsWidth += itemLabel.offsetWidth;
        // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
        itemsRef.current[i].current = itemLabel;
      }
    }
  }

  return {visibleItemsWidth, lastVisibleItemIndex};
};

const computeFirstItemOverflowText = (
  selectedItems: (MeasurableItem | null)[],
  visibleItemsCount: number,
  itemsRef: React.MutableRefObject<React.MutableRefObject<HTMLSpanElement>[]>,
  inputRef: React.RefObject<HTMLDivElement | HTMLInputElement>,
): string => {
  // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
  if (!itemsRef.current[0].current) {
    return '';
  }

  // @ts-expect-error - Error after enabling noUncheckedIndexedAccess
  const itemElement = itemsRef.current[0].current;
  const label = itemElement.textContent ?? '';
  let i = 0;
  let result = '';

  // Note: Use only the initial size to calculate comparisons.
  // This algorithm places text in the DOM to generate length measurements.
  // The text itself can modify the input size, depending on the layout.
  const isInputSizeDefined = inputRef.current;
  const inputSize = isInputSizeDefined
    ? // eslint-disable-next-line @sail/ui-no-reflow
      inputRef.current.offsetWidth - TEXT_FIT_MARGIN
    : 0;

  // Add the label's characters one by one until text reaches input width
  while (i < label.length) {
    const hiddenItemsCount = getItemsCount(
      selectedItems.slice(visibleItemsCount),
    );
    itemElement.textContent = `${label.substring(0, i).trim()}...${
      hiddenItemsCount > 0 ? ` and ${hiddenItemsCount} more` : ''
    }`;

    // eslint-disable-next-line @sail/ui-no-reflow
    if (isInputSizeDefined && itemElement.offsetWidth >= inputSize) {
      const truncated = label.substring(0, i - 1).trim();
      result = truncated ? `${truncated}...` : '';
      break;
    }
    i++;
  }

  return result;
};

export const getVisibleItemsAndOverflow = <T extends HTMLElement>(
  selectedItems: (MeasurableItem | null)[],
  itemsContainerRef: React.RefObject<HTMLDivElement | HTMLInputElement>,
  itemsRef: React.MutableRefObject<React.MutableRefObject<HTMLSpanElement>[]>,
  parentRef?: React.RefObject<T>,
  inputTextStyles?: Record<string, string>,
): {
  visibleItemsCount: number;
  firstItemOverflows: boolean;
  firstItemOverflowText: string;
} => {
  const itemsContainer = document.createElement('div');
  if (inputTextStyles) {
    Object.assign(itemsContainer.style, inputTextStyles);
  }
  (parentRef ?? itemsContainerRef).current!.appendChild(itemsContainer);

  const availableWidth =
    // eslint-disable-next-line @sail/ui-no-reflow
    itemsContainerRef.current!.offsetWidth - TEXT_FIT_MARGIN;
  const {visibleItemsWidth, lastVisibleItemIndex} =
    getInitialVisibleItemsParams(
      selectedItems,
      availableWidth,
      itemsRef,
      itemsContainer,
    );

  let visibleItemsCount = lastVisibleItemIndex + 1;
  let firstItemOverflows = false;
  let firstItemOverflowText = '';

  if (selectedItems.length === 1 && itemsRef.current[0]?.current) {
    firstItemOverflows =
      // eslint-disable-next-line @sail/ui-no-reflow
      itemsRef.current[0].current.offsetWidth > availableWidth;
  } else if (lastVisibleItemIndex + 1 !== selectedItems.length) {
    // If there are hidden items, make space for counter
    const counterWidth =
      calculateCounterWidth(selectedItems, itemsContainer) +
      VISIBLE_ITEMS_AND_COUNTER_GAP;
    // No item labels are visible if minimum first label width + counter don't
    // fit — that's an overflow of the first item too.
    if (MIN_FIRST_LABEL_WIDTH + counterWidth > availableWidth) {
      visibleItemsCount = 0;
      firstItemOverflows = true;
    } else {
      // Subtract current visible items width and counter width from current available width
      let updatedAvailableWidth =
        availableWidth - visibleItemsWidth - counterWidth;
      // Add item width from last item to second item until [visible items + counter] fit in input (first item is always visible)
      for (
        let i = lastVisibleItemIndex;
        i > 0 && updatedAvailableWidth < 0;
        i--
      ) {
        visibleItemsCount -= 1;
        // eslint-disable-next-line @sail/ui-no-reflow
        updatedAvailableWidth += itemsRef.current[i]?.current?.offsetWidth ?? 0;
      }

      // First item's label overflows if there is still not enough space after removing the rest of the items
      firstItemOverflows = updatedAvailableWidth < 0;
    }
  }

  if (firstItemOverflows) {
    firstItemOverflowText = computeFirstItemOverflowText(
      selectedItems,
      visibleItemsCount,
      itemsRef,
      itemsContainerRef,
    );
  }

  // Delete container with temporary items
  itemsContainer.remove();

  return {visibleItemsCount, firstItemOverflows, firstItemOverflowText};
};
